/**
 * Who is looking, answered once for every part of the app that asks (F-139, F-140).
 *
 * Leo, 2026-09-17: *"i got a refresh notice at that state and it brought me back to login
 * screen BUT with a logged in menu i could go to my plan and describe it."*
 *
 * **The session had survived; the two halves of the screen disagreed about it.** The shell
 * decides what to show from `session.signedIn` and every guarded screen decided the same
 * way, so those agreed — but `/entrar` was mounted outside the guard and rendered the
 * sign-in form to anybody who asked for it, session or no session. Reload an installed app
 * sitting on that route (which is where signing in used to leave you) and you get exactly
 * what he saw: a login form under a menu full of the sections you are signed in to. Nothing
 * was broken about the cookie, and nothing about it was visible as a fault to the code —
 * one route simply never asked the question.
 *
 * So the question is asked in one place and answered by one function. The shell reads
 * `showsSignedInChrome`, the guard reads `guardedView`, the sign-in route reads
 * `signInView`, and `session.test.ts` asserts the property that was violated: no view that
 * renders a sign-in form can coexist with a shell that is showing signed-in chrome.
 *
 * **What no test here can see is that `App.tsx` calls any of this.** The original fault was
 * wiring — a route mounted without the question — and wiring is exactly what this repository
 * cannot assert about the browser package: components need a DOM and a renderer, nothing in
 * `tsconfig.test.json` compiles a `.tsx` file, and re-mounting the bug leaves the suite
 * green. These functions are the place the answer lives so that it is one answer rather than
 * four; the tests hold the answer, and a reader holds the wiring.
 *
 * Pure, and in its own module rather than inside `App.tsx`, for `install/route.ts`'s reason:
 * a decision that lives inside a component can only be checked by looking at it.
 */

/**
 * What `/api/auth/me` answers.
 *
 * Declared here rather than in `api.ts` so the rules below can be tested without a DOM —
 * `api.ts` is `fetch` and nothing else. `api.ts` re-exports it, so every screen still has
 * one import for the call and its answer.
 */
export interface Session {
  signedIn: boolean;
  userId?: string;
  locale?: string;
  /**
   * The address this account signs in with, when the API could read it (F-140).
   *
   * Leo: *"I cant tell whether im logged in or not, nor with what email."* It matters more
   * here than in most products, because the way in is a code mailed to an address: a
   * learner with two addresses genuinely holds two accounts, and the objectives, the plan
   * and the schedule all hang off which one is signed in.
   *
   * Optional because it can honestly be missing — an account made by the retired phone
   * channel may hold none, and a failed read answers "signed in" without one rather than
   * signing somebody out over a lookup. The screens say "Signed in" in that case and never
   * invent an address.
   */
  email?: string;
  /**
   * What this learner's tier entitles them to, and what is left of this month's research grant
   * (F-151).
   *
   * **A fact the app holds before anything is typed**, which is the point of it riding on this
   * answer rather than on a route of its own: the design says a refusal a learner reaches is a
   * bug, and a screen that has to fetch the cap separately renders the create form — and the
   * research button — before it knows whether either is allowed.
   *
   * Optional because an older API answers without it, and because this type is what a failed
   * `/api/auth/me` falls back to. Nothing here should read a missing entitlement as "no
   * entitlement"; the API is the authority and the refusal is still enforced there.
   */
  entitlement?: Entitlement;
}

/** The shape `GET /api/auth/me` carries. Mirrors `EntitlementView` in `packages/api`. */
export interface Entitlement {
  tier: number;
  /** Objectives that may be live at once. */
  activeObjectives: number;
  /** Research runs the tier's grant covers in a month. Grants do not roll over. */
  researchRunsPerMonth: number;
  research: 'basic' | 'advanced';
  researchRunsUsed: number;
  researchRunsLeft: number;
  /** `YYYY-MM`, in the learner's own zone, so a screen can say *this month* and mean it. */
  month: string;
}

/** The three answers, and `unknown` is not a synonym for signed out. */
export type Viewer =
  /**
   * The cookie has not been asked about yet.
   *
   * It is HttpOnly by design, so the page genuinely cannot know without a round trip, and a
   * flash of "you are not signed in" at somebody who is is alarming in a way a moment of
   * blankness is not.
   */
  | 'unknown'
  | 'signedOut'
  | 'signedIn';

/** Every value, so a test can be exhaustive rather than illustrative. */
export const VIEWERS: readonly Viewer[] = ['unknown', 'signedOut', 'signedIn'];

export function viewerOf(session: Session | undefined): Viewer {
  if (session === undefined) return 'unknown';
  return session.signedIn ? 'signedIn' : 'signedOut';
}

/**
 * Whether the shell shows the menu, the chat tab and the rest of the signed-in furniture.
 *
 * The menu is the thing Leo could see working while the page under it asked him to sign in,
 * so it is the half of the contradiction the test names.
 */
export function showsSignedInChrome(viewer: Viewer): boolean {
  return viewer === 'signedIn';
}

/** What a screen behind the guard renders. */
export type GuardedView = 'waiting' | 'signIn' | 'content';

export function guardedView(viewer: Viewer): GuardedView {
  if (viewer === 'unknown') return 'waiting';
  return viewer === 'signedIn' ? 'content' : 'signIn';
}

/** What the sign-in route renders. */
export type SignInView =
  | 'waiting'
  | 'signIn'
  /**
   * Somebody who is already signed in is sent on rather than shown the form.
   *
   * `replace`, at the call site: the sign-in route must not sit in the history behind the
   * app, or the back gesture lands on a form that immediately bounces again.
   */
  | 'elsewhere';

export function signInView(viewer: Viewer): SignInView {
  if (viewer === 'unknown') return 'waiting';
  return viewer === 'signedIn' ? 'elsewhere' : 'signIn';
}

/**
 * The address to show, or nothing — never a placeholder.
 *
 * An invented "unknown" beside "Signed in as" is worse than saying nothing at all: the
 * whole point of showing this is that a learner with two addresses can tell which account
 * they are in, and a word that is not an address answers that question wrongly.
 */
export function signedInAs(session: Session | undefined): string | undefined {
  if (viewerOf(session) !== 'signedIn') return undefined;
  const email = session?.email;
  return email && email.trim() ? email : undefined;
}
